import { useCallback } from 'react'

import { useTheme } from './ThemeProvider'

/**
 * Extended dark mode hook with additional utilities
 *
 * This hook provides a simplified interface for dark mode control,
 * building on top of the ThemeProvider context.
 *
 * @example
 * ```tsx
 * const { isDark, toggle, setDark, setLight } = useDarkMode();
 *
 * return (
 *   <button onClick={toggle}>
 *     {isDark ? 'Light' : 'Dark'} Mode
 *   </button>
 * );
 * ```
 */
interface UseDarkModeReturn {
  /** Whether dark mode is currently active */
  isDark: boolean
  /** Whether light mode is currently active */
  isLight: boolean
  /** Current theme mode ('light', 'dark', or 'system') */
  mode: 'light' | 'dark' | 'system'
  /** Whether theme is currently transitioning */
  isTransitioning: boolean
  /** Toggle between light and dark mode */
  toggle: () => void
  /** Set dark mode */
  setDark: () => void
  /** Set light mode */
  setLight: () => void
  /** Set system mode (follow OS preference) */
  setSystem: () => void
  /** Set specific mode */
  setMode: (mode: 'light' | 'dark' | 'system') => void
}

/**
 * useDarkMode hook
 *
 * Simplified dark mode control hook.
 */
export const useDarkMode = (): UseDarkModeReturn => {
  const theme = useTheme()

  const toggle = useCallback(() => {
    theme.toggleTheme()
  }, [theme])

  const setDark = useCallback(() => {
    theme.setDarkMode()
  }, [theme])

  const setLight = useCallback(() => {
    theme.setLightMode()
  }, [theme])

  const setSystem = useCallback(() => {
    theme.setSystemMode()
  }, [theme])

  const setMode = useCallback(
    (mode: 'light' | 'dark' | 'system') => {
      theme.setMode(mode)
    },
    [theme],
  )

  return {
    isDark: theme.resolvedTheme === 'dark',
    isLight: theme.resolvedTheme === 'light',
    mode: theme.mode,
    isTransitioning: theme.isTransitioning,
    toggle,
    setDark,
    setLight,
    setSystem,
    setMode,
  }
}
